import { z } from "zod";
import { ACCOUNT_TYPES, CONTENT_STATUSES, CONTENT_TYPES, DIRT_TALK_STATUSES, DIRT_TALK_VARIANTS, PROJECT_TYPES, QUOTE_REQUEST_STATUSES, RIG_OF_WEEK_STATUSES, SAVED_ITEM_TYPES, SOURCE_PLATFORMS, SOURCE_RUN_STATUSES, SOURCE_TYPES, USER_ROLES, } from "../constants/index.js";
const isoDate = z.iso.datetime();
const slug = z.string().regex(/^[a-z0-9-]+$/, "must be lowercase kebab-case").min(1).max(80);
const postcode = z.string().regex(/^\d{4}$/, "must be a 4-digit Australian postcode");
export const EquipmentTagSchema = z.object({
    tractrsId: z.string().min(1),
    slug: z.string().min(1),
    make: z.string().min(1),
    model: z.string().min(1),
    confidence: z.number().min(0).max(1),
    confirmed: z.boolean(),
});
export const CompanySchema = z.object({
    id: z.string(),
    slug: z.string().nullable(),
    name: z.string().min(1).max(120),
    abn: z.string().nullable(),
    website: z.url().nullable(),
    description: z.string().nullable(),
    logoUrl: z.string().nullable(),
    promoteContent: z.boolean(),
    createdAt: isoDate,
});
// Public-facing company profile (the /company/:slug landing page). Only the
// brand-safe fields — no ABN, no staff, no internal flags.
export const CompanyPublicSchema = z.object({
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    website: z.url().nullable(),
    logoUrl: z.string().nullable(),
});
export const CompanySignupSchema = z.object({
    name: z.string().min(1).max(120),
    abn: z.string().regex(/^\d{11}$/, "ABN must be 11 digits").optional(),
    website: z.url().optional(),
});
export const UserSchema = z.object({
    id: z.string(),
    email: z.email(),
    name: z.string().min(1),
    googleId: z.string().nullable(),
    role: z.enum(USER_ROLES),
    accountType: z.enum(ACCOUNT_TYPES),
    companyId: z.string().nullable(),
    createdAt: isoDate,
});
export const SavedItemSchema = z.object({
    id: z.string(),
    userId: z.string(),
    itemType: z.enum(SAVED_ITEM_TYPES),
    itemId: z.string(),
    note: z.string().max(500).nullable(),
    createdAt: isoDate,
});
export const SavedItemCreateSchema = z.object({
    itemType: z.enum(SAVED_ITEM_TYPES),
    itemId: z.string().min(1),
    note: z.string().max(500).optional(),
});
export const ContentItemSchema = z.object({
    id: z.string(),
    slug,
    title: z.string().min(1),
    description: z.string().nullable(),
    type: z.enum(CONTENT_TYPES),
    category: z.string().nullable(),
    status: z.enum(CONTENT_STATUSES),
    sourceUrl: z.url().nullable(),
    embedUrl: z.url().nullable(),
    thumbnailUrl: z.url().nullable(),
    durationSec: z.number().int().nonnegative().nullable(),
    sourcePlatform: z.enum(SOURCE_PLATFORMS),
    sourceAuthor: z.string().nullable(),
    aiRelevanceScore: z.number().min(0).max(1).nullable(),
    aiQualityScore: z.number().min(0).max(1).nullable(),
    aiReason: z.string().nullable(),
    sponsored: z.boolean(),
    sponsorName: z.string().nullable(),
    featured: z.boolean(),
    publishedAt: isoDate.nullable(),
    scheduledAt: isoDate.nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    postcode: postcode.nullable(),
    region: z.string().nullable(),
    tags: z.array(z.string()),
    approvedById: z.string().nullable(),
    approvedAt: isoDate.nullable(),
    equipmentTags: z.array(EquipmentTagSchema),
    createdAt: isoDate,
    updatedAt: isoDate,
});
export const ContentListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
    type: z.enum(CONTENT_TYPES).optional(),
    equipmentSlug: z.string().optional(),
    postcode: postcode.optional(),
    region: z.string().optional(),
    tag: z.string().optional(),
});
export const SourceSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    type: z.enum(SOURCE_TYPES),
    url: z.string().min(1),
    cronSchedule: z.string().min(1).default("0 */6 * * *"),
    config: z.record(z.string(), z.unknown()).default({}),
    enabled: z.boolean(),
    lastRunAt: isoDate.nullable(),
    retryCount: z.number().int().nonnegative(),
    nextRetryAt: isoDate.nullable(),
});
export const SourceRunSchema = z.object({
    id: z.string(),
    sourceId: z.string(),
    status: z.enum(SOURCE_RUN_STATUSES),
    itemsCreated: z.number().int().nonnegative(),
    itemsDuplicated: z.number().int().nonnegative(),
    startedAt: isoDate,
    completedAt: isoDate.nullable(),
    errorMessage: z.string().nullable(),
});
export const DirtTalkSchema = z.object({
    id: z.string(),
    date: z.iso.date(),
    variant: z.enum(DIRT_TALK_VARIANTS),
    title: z.string().min(1),
    body: z.string().min(1),
    imageUrl: z.url().nullable(),
    equipmentTag: EquipmentTagSchema.nullable(),
    status: z.enum(DIRT_TALK_STATUSES),
    approvedById: z.string().nullable(),
    approvedAt: isoDate.nullable(),
    publishedAt: isoDate.nullable(),
});
export const RigOfWeekCandidateSchema = z.object({
    aiScore: z.number().min(0).max(1),
    rationale: z.string().min(1),
    operatorName: z.string().min(1),
    operatorHandle: z.string().nullable(),
    equipmentSummary: z.string().min(1),
    imageUrl: z.url().nullable(),
});
export const RigOfWeekSchema = z.object({
    id: z.string(),
    weekStarting: z.iso.date(),
    candidates: z.array(RigOfWeekCandidateSchema),
    selectedOperatorName: z.string().nullable(),
    selectedOperatorHandle: z.string().nullable(),
    rigDescription: z.string().nullable(),
    rigImageUrl: z.url().nullable(),
    isSponsored: z.boolean(),
    sponsorName: z.string().nullable(),
    status: z.enum(RIG_OF_WEEK_STATUSES),
    approvedById: z.string().nullable(),
    approvedAt: isoDate.nullable(),
    publishedAt: isoDate.nullable(),
});
export const OperatorSchema = z.object({
    id: z.string(),
    slug,
    name: z.string().min(1),
    handle: z.string().nullable(),
    postcode,
    region: z.string().min(1),
    bio: z.string().nullable(),
    avatarUrl: z.url().nullable(),
    equipmentSummary: z.string().nullable(),
    contentCount: z.number().int().nonnegative(),
});
export const QuoteRequestSubmitSchema = z.object({
    name: z.string().min(2).max(100),
    email: z.email(),
    phone: z.string().min(6).max(20),
    postcode,
    projectType: z.enum(PROJECT_TYPES),
    scaleHint: z.string().max(120).optional(),
    description: z.string().min(10).max(2000),
    honeypot: z.string().max(0).optional(),
});
export const QuoteRequestSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.email(),
    phone: z.string(),
    postcode,
    projectType: z.enum(PROJECT_TYPES),
    scaleHint: z.string().nullable(),
    description: z.string(),
    trukEstimateLowAud: z.number().nullable(),
    trukEstimateHighAud: z.number().nullable(),
    trukEstimateConfidence: z.number().min(0).max(1).nullable(),
    trukCalculationVersion: z.string().nullable(),
    status: z.enum(QUOTE_REQUEST_STATUSES),
    assignedOperatorId: z.string().nullable(),
    drewNotes: z.string().nullable(),
    createdAt: isoDate,
});
export const PostcodeProfileSchema = z.object({
    id: z.string(),
    postcode,
    suburb: z.string().min(1),
    region: z.string().min(1),
    contentCount: z.number().int().nonnegative(),
    operatorCount: z.number().int().nonnegative(),
    lastBuiltAt: isoDate.nullable(),
});
export const MagicLinkRequestSchema = z.object({
    email: z.email(),
    // Optional JIT signup payload — when present, verify creates the user
    // (and Company if accountType=COMPANY). Ignored if a user already exists
    // for the email.
    name: z.string().min(1).max(100).optional(),
    accountType: z.enum(ACCOUNT_TYPES).optional(),
    company: CompanySignupSchema.optional(),
});
export const NotificationRecipientsSchema = z.object({
    recipients: z.array(z.email()).min(1),
});
export const ContactSubmitSchema = z.object({
    name: z.string().min(2).max(100),
    email: z.email(),
    message: z.string().min(10).max(2000),
    honeypot: z.string().max(0).optional(),
});
export const TruktEstimateRequestSchema = z.object({
    projectType: z.enum(PROJECT_TYPES),
    scaleM3: z.number().positive().nullable(),
    postcode,
    machineCategoryHint: z.string().nullable(),
    urgency: z.enum(["low", "normal", "high"]).nullable(),
});
export const TrukEstimateResponseSchema = z.object({
    estimateLowAud: z.number().nonnegative(),
    estimateHighAud: z.number().nonnegative(),
    confidence: z.number().min(0).max(1),
    calculationVersion: z.string(),
    factors: z.array(z.string()).optional(),
});
export const TractrsMachinerySearchHitSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    model: z.string().nullable(),
    brand: z.object({ name: z.string(), slug: z.string() }).nullable(),
    category: z.object({ name: z.string(), slug: z.string() }).nullable(),
    primaryImageUrl: z.url().nullable(),
});
export const TractrsNewsItemSchema = z.object({
    id: z.string(),
    title: z.string().min(1),
    snippet: z.string().nullable(),
    // Tractrs's CDN-cached image URL once the image_cache cron has processed
    // this row. Until then, this is the publisher's original URL (which may be
    // CORP-blocked — frontend should still proxy these). When `imageWidth` is
    // populated, it's safe to assume `imageUrl` is the cached/CORS-friendly copy.
    imageUrl: z.url().nullable(),
    // Pro endpoint only — original publisher URL, kept for attribution.
    imageOriginalUrl: z.url().nullable().optional(),
    // Populated by Tractrs's hourly image-cache cron. Null = not cached yet.
    imageWidth: z.number().int().nullable().optional(),
    imageHeight: z.number().int().nullable().optional(),
    imageContentType: z.string().nullable().optional(),
    sourceUrl: z.url(),
    publishedAt: z.iso.datetime().nullable(),
    detectionType: z.enum(["new_release", "existing_update"]),
    extractedBrand: z.string().nullable(),
    extractedModel: z.string().nullable().optional(),
    extractedYear: z.number().int().nullable().optional(),
    category: z.string().nullable(),
    matchedMachinery: z
        .object({
        slug: z.string(),
        name: z.string(),
        brand: z.string().optional().nullable(),
        category: z.string().optional().nullable(),
    })
        .nullable(),
    createdAt: z.iso.datetime(),
});
export const TractrsNewsListResponseSchema = z.object({
    data: z.array(TractrsNewsItemSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
});
export const GeminiContentScoreSchema = z.object({
    relevance: z.number().min(0).max(1),
    quality: z.number().min(0).max(1),
    reason: z.string().min(1).max(500),
});
export const GeminiEquipmentDetectSchema = z.object({
    candidates: z.array(z.object({
        make: z.string().min(1),
        model: z.string().min(1),
        confidence: z.number().min(0).max(1),
    })),
});
export const GeminiLocationSchema = z.object({
    postcode: z.string().regex(/^\d{4}$/).nullable(),
    region: z.string().nullable(),
});
export const SeoMetaSchema = z.object({
    title: z.string().min(1).max(70),
    description: z.string().min(1).max(160),
    canonicalUrl: z.url(),
    ogImageUrl: z.url(),
    jsonLd: z.array(z.record(z.string(), z.unknown())).default([]),
});
//# sourceMappingURL=index.js.map